import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { subscriptionDataService } from '../services/subscriptionService.js';
import { db } from '../firebase';

const CheckoutForm = ({ plan, employeeId, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js n'est pas encore chargé
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Étape 1: Créer le payment method avec Stripe côté client
      const cardElement = elements.getElement(CardElement);
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          // Vous pouvez ajouter des détails de facturation ici si nécessaire
        },
      });

      if (pmError) {
        setError(pmError.message);
        setLoading(false);
        return;
      }

      console.log('✅ Payment method créé:', paymentMethod.id);

      // Étape 2: Créer l'abonnement via notre Cloud Function en passant le paymentMethodId
      const result = await subscriptionDataService.createSubscriptionPayment(
        db,
        employeeId,
        plan.id,
        paymentMethod.id  // Passer le payment method ID
      );

      console.log('Payment Intent:', result.clientSecret);

      // Étape 3: Confirmer le paiement avec Stripe
      console.log('✅ Paiement confirmé:', result);

      // Gérer les différents cas de client_secret
      if (!result.clientSecret) {
        console.log('⚠️ Client secret null - Abonnement créé mais paiement non immédiat');
        
        // Si l'abonnement a été créé avec succès mais sans paiement immédiat
        if (result.success && result.subscriptionId) {
          console.log('✅ Abonnement créé avec statut:', result.status);
          
          // Afficher un message d'information
          setError('Votre abonnement a été créé avec succès. Le paiement sera traillé lors de votre première facturation.');
          
          // Attendre un peu puis rediriger vers le succès
          setTimeout(() => {
            onSuccess(result);
          }, 2000);
        } else {
          console.error('❌ Erreur client secret:', result.clientSecret);
          setError('Erreur lors de la création du paiement. Veuillez réessayer.');
        }
        return;
      }

      // Si client_secret est disponible, procéder à la confirmation du paiement
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(result.clientSecret);

      if (stripeError) {
        console.error('❌ Erreur Stripe lors de la confirmation:', stripeError);
        setError(stripeError.message);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Paiement réussi
        console.log('✅ Paiement réussi !');
        onSuccess(result);
      } else {
        console.log('⚠️ Statut du paiement:', paymentIntent.status);
        // Le paiement peut être en attente, on considère quand même le succès
        onSuccess(result);
      }
    } catch (err) {
      console.error('Erreur lors du paiement:', err);
      setError(err.message || 'Erreur lors du traitement du paiement. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informations du plan */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {plan.description}
            </p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {plan.price === 0 ? 'Gratuit' : `€${(plan.price / 100).toFixed(2)}`}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {plan.period === 'an' ? 'par an' : 'par mois'}
            </p>
          </div>
        </div>
      </div>

      {/* Informations de carte */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Informations de carte
        </label>
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800">
          <CardElement options={cardElementOptions} />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Les paiements sont sécurisés par Stripe. Aucune information de carte n'est stockée sur nos serveurs.
        </p>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Boutons d'action */}
      <div className="flex space-x-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Traitement...
            </div>
          ) : (
            `Payer ${plan.price === 0 ? 'Gratuit' : `€${(plan.price / 100).toFixed(2)}`}`
          )}
        </button>
      </div>

      {/* Informations de sécurité */}
      <div className="text-center">
        <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span>Paiement sécurisé par Stripe</span>
        </div>
      </div>
    </form>
  );
};

export default CheckoutForm;
